import PropTypes from "prop-types";
import React from "react";
import classNames from "classnames";
import { SearchIcon } from "wm-graphics";
import { TextField } from "../TextField/TextField";
import { SearchOption } from "./SearchOption/SearchOption";
import { Slide } from "../../animations/Slide/Slide";
import { Text } from "../Text/Text";
import { Spinner } from "../Spinner/Spinner";
import { withTranslations } from "../../../utils/withTranslations";
import defaultLocales from "./locales";
import "./SearchField.scss";

const SEARCH_DELAY = 1200;

export class USearchField extends React.Component {
  static defaultProps = {
    delay: SEARCH_DELAY,
    defaultSearchTerm: "",
    showSearchIcon: true,
  };

  static propTypes = {
    /** Additional class names for the component */
    className: PropTypes.string,
    /** If true, we need a search delay before we run the onSearch */
    delay: PropTypes.number,
    /** Default search term shown */
    defaultSearchTerm: PropTypes.string,
    /** If true the spinner is displayed in the place of the load more button */
    loading: PropTypes.bool,
    /** Locales object for rendering text */
    locales: PropTypes.object,
    /** Options to search through */
    options: PropTypes.array,
    /** Callback function to search */
    onSearch: PropTypes.func,
    /** Callback function to load more options */
    onLoad: PropTypes.func,
    /** Callback for when an option is clicked */
    onOptionClick: PropTypes.func,
    /** Place Holder associated with component */
    placeholder: PropTypes.string,
    /** Customize option render */
    renderOption: PropTypes.func,
    /** Whether or not to show search icon */
    showSearchIcon: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
      searchTerm: props.defaultSearchTerm,
    };

    this.timeout = null;
    this.fieldRef = React.createRef();
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOutside);
  }

  onFocus = () => {
    this.setIsOpen(true);
  };

  onClick = () => {
    this.setIsOpen(true);
  };

  onOptionClick = (option) => {
    const { onOptionClick } = this.props;
    this.onSearch(option.label);
    if (onOptionClick) {
      onOptionClick(option);
    }
    this.setState({ isOpen: false });
  };

  onSearch = (searchTerm) => {
    const { delay, onSearch } = this.props;

    if (delay !== 0) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(onSearch, SEARCH_DELAY, searchTerm);
    } else if (onSearch) {
      onSearch(searchTerm);
    }
    this.setState({ searchTerm });
  };

  onKeyDownHandler = (event) => {
    if (event.key === "Enter") {
      this.onClick();
    }
  };

  onLoadKeyDownHandler = (event) => {
    const { onLoad } = this.props;
    if (event.key === "Enter") {
      onLoad();
    }
  };

  handleClickOutside = (event) => {
    if (this.fieldRef && !this.fieldRef.current.contains(event.target)) {
      this.setIsOpen(false);
    }
  };

  setIsOpen = (newIsOpen) => {
    if (newIsOpen) {
      document.addEventListener("mousedown", this.handleClickOutside);
    } else {
      document.removeEventListener("mousedown", this.handleClickOutside);
    }

    this.setState({ isOpen: newIsOpen });
  };

  renderLoadButton = () => {
    const { loading, locales, onLoad } = this.props;

    return loading ? (
      <div className="SearchField__load">
        <Spinner />
      </div>
    ) : (
      <div
        onClick={onLoad}
        onKeyDown={this.onLoadKeyDownHandler}
        tabIndex={0}
        role="button"
      >
        <Text type="H6" className="SearchField__load">
          {locales.loadMore}
        </Text>
      </div>
    );
  };

  renderSearchBar = () => {
    const { placeholder, showSearchIcon } = this.props;
    const { searchTerm } = this.state;

    return (
      <>
        {showSearchIcon && (
          <div className="Search__icon">
            <SearchIcon height={16} width={16} />
          </div>
        )}
        <TextField
          onClick={this.onClick}
          onKeyDown={this.onKeyDownHandler}
          placeholder={placeholder}
          onChange={this.onSearch}
          defaultValue={searchTerm}
          onFocus={this.onFocus}
        />
      </>
    );
  };

  render() {
    const { className, onLoad, options, renderOption, showSearchIcon } =
      this.props;
    const { isOpen } = this.state;

    const searchFieldClass = classNames(
      "SearchField",
      { "SearchField--withLoadMore": onLoad },
      { "SearchField--noSearchIcon": !showSearchIcon },
      className
    );

    return (
      <div className={searchFieldClass} ref={this.fieldRef}>
        {this.renderSearchBar()}
        {options && options.length > 0 && (
          <Slide isVisible={isOpen} timeout={100}>
            <div className="SearchField__list" data-testid="searchListOptions">
              {options.map((option, index) => (
                <SearchOption
                  key={index}
                  option={option}
                  onClick={this.onOptionClick}
                  renderOption={renderOption}
                  onLoad={!!onLoad}
                />
              ))}
            </div>
            {onLoad && this.renderLoadButton()}
          </Slide>
        )}
      </div>
    );
  }
}

const translatedSearchField = withTranslations(defaultLocales)(USearchField);

export { translatedSearchField as SearchField };

export default translatedSearchField;
